import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import TimezoneService from '../../api/services/config/timezone/TimezoneService';

// -- Type definitions --

export interface TimezoneCoordinates {
  lat: number;
  lng: number;
}

export interface TimezoneSelectorProps {
  value?: string | null;
  onSelect: (value: string | null) => void;
  label: string;
  coordinates?: TimezoneCoordinates | null;
  disabled?: boolean;
  name?: string;
}

// -- Helpers --

/** Get the UTC offset label from the offset minutes */
function minutesToUtcLabel(offsetMinutes: number): string {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;

  if (minutes === 0) {
    return `UTC${sign}${hours}`;
  }

  return `UTC${sign}${String(hours).padStart(2, '0')}:${String(
    minutes
  ).padStart(2, '0')}`;
}

/** Get the timezone offset minutes from the timezone */
function getTimezoneOffsetMinutes(timeZone: string, date = new Date()): number {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const localDate = new Date(date.toLocaleString('en-US', { timeZone }));
  return Math.round((localDate.getTime() - utcDate.getTime()) / 60000);
}

/** Format the UTC offset from the timezone */
function formatUtcOffset(timeZone: string): string {
  // -- Try to get the offset from the Intl.DateTimeFormat API --
  try {
    const offset = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value;

    if (offset) {
      return offset.replace(/^GMT/i, 'UTC');
    }
  } catch {
    // -- continue silently to manual offset fallback --
  }

  try {
    return minutesToUtcLabel(getTimezoneOffsetMinutes(timeZone));
  } catch {
    // -- give up --
    return '';
  }
}

/** Parse the timezone parts into continent and location */
function parseTimezoneParts(timeZone: string): {
  continent: string;
  location: string;
} {
  const slashIndex = timeZone.indexOf('/');

  if (slashIndex === -1) {
    return { continent: '', location: timeZone };
  }

  return {
    continent: timeZone.slice(0, slashIndex),
    location: timeZone.slice(slashIndex + 1),
  };
}

/** Sort the timezones by continent, offset, and location */
function sortTimezones(timezones: string[]): string[] {
  const offsetCache = new Map<string, number>();

  const getOffset = (timeZone: string): number => {
    const cached = offsetCache.get(timeZone);
    if (cached !== undefined) {
      return cached;
    }

    try {
      const offset = getTimezoneOffsetMinutes(timeZone);
      offsetCache.set(timeZone, offset);
      return offset;
    } catch {
      offsetCache.set(timeZone, 0);
      return 0;
    }
  };

  return [...timezones].sort((a, b) => {
    const partsA = parseTimezoneParts(a);
    const partsB = parseTimezoneParts(b);

    const continentA = partsA.continent || '\uFFFF';
    const continentB = partsB.continent || '\uFFFF';
    const continentCompare = continentA.localeCompare(continentB);
    if (continentCompare !== 0) {
      return continentCompare;
    }

    const offsetCompare = getOffset(a) - getOffset(b);
    if (offsetCompare !== 0) {
      return offsetCompare;
    }

    return partsA.location.localeCompare(partsB.location);
  });
}

/** Render the UTC offset label for the timezone */
function UtcOffsetLabel({ timeZone }: { timeZone: string }) {
  const offset = formatUtcOffset(timeZone);

  if (!offset) {
    return null;
  }

  return (
    <Typography
      component="span"
      variant="body2"
      color="text.secondary"
      sx={{ flexShrink: 0, ml: 1 }}
    >
      {offset}
    </Typography>
  );
}

// -- Component --

function TimezoneSelector({
  value = null,
  onSelect,
  label,
  coordinates = null,
  disabled = false,
  name = 'timezone',
}: TimezoneSelectorProps) {
  const [timezones, setTimezones] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const requestIdRef = useRef(0);
  const valueRef = useRef(value);
  const onSelectRef = useRef(onSelect);
  const initialValueRef = useRef(value);
  const hasCompletedInitialFetchRef = useRef(false);

  valueRef.current = value;
  onSelectRef.current = onSelect;

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);

    const params = coordinates
      ? { latitude: coordinates.lat, longitude: coordinates.lng }
      : undefined;

    TimezoneService.getTimezones(params)
      .then(({ timezones: options, suggested }) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setTimezones(sortTimezones(options));

        const isInitialFetch = !hasCompletedInitialFetchRef.current;
        hasCompletedInitialFetchRef.current = true;

        if (!coordinates || !suggested || suggested === valueRef.current) {
          return;
        }

        // Keep a pre-existing value on first fetch (edit mode); suggest on later coordinate changes
        if (isInitialFetch && initialValueRef.current) {
          return;
        }

        onSelectRef.current(suggested);
      })
      .catch((error) => {
        console.error('Error fetching timezones:', error);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates?.lat, coordinates?.lng]);

  const selectedOffset = value ? formatUtcOffset(value) : '';

  return (
    <Autocomplete
      disablePortal
      loading={loading}
      noOptionsText="Pas d'options de sélection"
      options={timezones}
      value={value ?? null}
      onChange={(_, selected) => {
        onSelect(selected);
      }}
      getOptionLabel={(option) => option}
      isOptionEqualToValue={(option, selected) => option === selected}
      disabled={disabled}
      slotProps={{
        listbox: {
          sx: { '& .MuiAutocomplete-option': { alignItems: 'flex-start' } },
        },
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <li key={key} {...optionProps}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                width: '100%',
              }}
            >
              <Typography component="span" variant="body2" sx={{ minWidth: 0 }}>
                {option}
              </Typography>
              <UtcOffsetLabel timeZone={option} />
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          name={name}
          helperText={selectedOffset || undefined}
          slotProps={{
            formHelperText: {
              sx: { color: 'text.secondary', mx: 0 },
            },
          }}
        />
      )}
    />
  );
}

export default TimezoneSelector;
